import { Typography } from '@mui/material';
import { GridSelectionModel, GRID_CHECKBOX_SELECTION_COL_DEF } from '@mui/x-data-grid-pro';
import { GridApiPro } from '@mui/x-data-grid-pro/models/gridApiPro';
import React, { useEffect, useMemo } from 'react';

import { gridDateTimeFormatter } from '@shared/commonFunctions';
import { dataGridCol } from '@shared/components/grid/common';
import { GridCell } from '@shared/components/grid/GridCell';
import { IPhoenixGridActions, PhoenixGrid } from '@shared/components/grid/PhoenixGrid';
import { EStipTypeEntity } from '@shared/interfaces';
import { colorTheme } from '@styles/muiTheme';

import { IStipsRequestListItem } from '../../../interfaces';
import { NEW_STIP } from '../StipRequestModal';

interface StipsTableProps {
  apiRef: React.MutableRefObject<GridApiPro>;
  rows: IStipsRequestListItem[];
  toggleSelectRow: (ids: GridSelectionModel) => void;
  selectedRowIds: GridSelectionModel;
  editingRowId: string | null;
  actions: IPhoenixGridActions;
}

export const StipsTable = ({
  apiRef,
  rows,
  selectedRowIds,
  toggleSelectRow,
  editingRowId,
  actions,
}: StipsTableProps) => {

  useEffect(() => {
    const defaultSelectedIds = rows
      .filter(row => !row.receivedDate)
      .map(row => row.id);


    if (JSON.stringify(defaultSelectedIds.sort()) !== JSON.stringify(selectedRowIds.sort())) {
      toggleSelectRow(defaultSelectedIds);
    }
  }, [rows, toggleSelectRow]);

  const columns = useMemo(() => [
    {
      ...GRID_CHECKBOX_SELECTION_COL_DEF,
      minWidth: 75,
      flex: 0.5,
      renderHeader: () => (
        <Typography
          fontWeight={700}
          color={colorTheme.palette.common.black}
        >
          Request
        </Typography>
      )
    },
    dataGridCol('stipTypeName', {
      minWidth: 200,
      flex: 1.3,
      headerName: 'Type',
    }),
    dataGridCol('relatedToEntity', {
      minWidth: 100,
      flex: 0.9,
      headerName: 'For',
      renderCell: ({ value, row }) => value === EStipTypeEntity.CONTACT ? row.isCoBorrower ? 'CoBorrower' : 'Borrower' : value
    }),
    dataGridCol('description', {
      minWidth: 250,
      flex: 1.3,
      headerName: 'Details',
    }),
    dataGridCol('receivedDate', {
      minWidth: 120,
      flex: 0.7,
      headerName: 'Status',
      renderCell: ({ value }) => (
        <GridCell
          text={value === NEW_STIP ? '' : value ? 'Received' : 'Not Received'}
          sx={{ color: value ? colorTheme.palette.success.dark : colorTheme.palette.error.main }}
        />
      ),
    }),
    dataGridCol('requestedDate', {
      minWidth: 100,
      flex: 0.9,
      headerName: 'Request Date',
      type: 'dateTime',
      valueFormatter: gridDateTimeFormatter,
    }),
    dataGridCol('additionalNote', {
      minWidth: 250,
      flex: 2,
      headerName: 'Note',
      editable: true,
    }),
  ].map((column) => ({ flex: 1, ...column })), []);

  return (
    <PhoenixGrid
      withHeader
      canNotCopy
      noButton
      checkboxSelection
      height="500px"
      pagination={false}
      editableRowId={editingRowId}
      selectionModel={selectedRowIds}
      apiRef={apiRef}
      actions={actions}
      columns={columns}
      rows={rows}
      onSelectionModelChange={(selectionMode) => toggleSelectRow(selectionMode)}
    />
  );
};